import { OperationAttrName } from '@common/prosemirror/operations/operations.constant';
import { OperationTag, OperationTagType, ParsedOperation } from '@common/prosemirror/operations/operations.type';
import { EditorState, Transaction } from 'prosemirror-state';
import { AttrStep, Step } from 'prosemirror-transform';

/** Creates an operation to change the current document. */
export function createOperation(state: EditorState, operationName: string, invertedOperationName: string, f: (tr: Transaction) => void): Transaction | undefined {
  // create transaction
  const tr = state.tr;

  /*
  Some operations involve multiple steps that must be applied or reverted as a single transaction.
  However, all local changes are sent to the server as a simple list of individual steps.
  To handle this, we need a way to group certain steps together as a single operation (transaction).

  To support these multi-step operations (transactions), we create additional steps that serve as start and end markers for operations:
    - 2 steps to mark the beginning and end of a direct operation. This approach allows us to identify and group steps that belong to the operation.
    - 2 steps to mark the beginning and end of a inverted operation. We include name of the inverted operation to enable ProseMirror editor plugins
      to validate operations and maintain consistent states.
    - 2 steps to clean up the document state by removing the aforementioned markers. This allows to get the correct start and end markers for inverted operations.

  For example, when renaming a variable that has two definitions, we need two steps (one for each definition).
  In this case, both steps must either be applied together or canceled together if there is a conflict with other changes
  (e.g., if someone adds a new definition for the same variable).
  */

  // create a step indicating start of the operation
  setOperationTag(tr, operationName, 'start');
  // create a step indicating end of the inverted operation
  setOperationTag(tr, invertedOperationName, 'end');
  // create a step to handle the inverted operation correctly
  resetOperationTag(tr);

  const stepsCount = tr.steps.length;
  // add real operation steps
  f(tr);
  // when the transaction has no new steps then nothing to do
  if (stepsCount === tr.steps.length)
    return;

  // create a step indicating end of the operation
  setOperationTag(tr, operationName, 'end');
  // create a step indicating start of the inverted operation
  setOperationTag(tr, invertedOperationName, 'start');
  // create a step to handle the inverted operation correctly
  resetOperationTag(tr);

  return tr;
}

/** Splits an array of steps into operations.
 * Returns an array of operations
 * if each step belongs to an operation
 * and at least one operation is formed.
 * Otherwise, returns a null value. */
export function parseOperations(steps: Step[]): ParsedOperation[] | null {
  if (!steps?.length)
    return null;
  const operations: ParsedOperation[] = [];

  let startTag: OperationTag = null;
  let endTag: OperationTag = null;

  for (let i = 0; i < steps.length;) {
    let startStepIndex = i, endStepIndex;
    // analyze the current step
    startTag = parseOperationTag(steps[i++]);

    // if the current step is not the start operation tag, then stop parsing
    if (!(startTag && isStartOperationTag(startTag)))
      return null;

    const invertedOperationEnd = parseOperationTag(steps[i++])
    // if the next step is not the end operation tag of the inverted operation, then stop parsing
    if (!(invertedOperationEnd && !isStartOperationTag(invertedOperationEnd)))
      return null;
    // if the next step is not reset operation tag, then stop parsing
    if (!isResetOperationStep(steps[i++]))
      return null;

    // search the end operation tag
    while (i < steps.length && !endTag) {
      endStepIndex = i++;
      endTag = parseOperationTag(steps[endStepIndex]);
    }

    // if the end operation tag is not found, then stop parsing
    if (!endTag)
      return null;

    // if the end operation tag is not suitable for the start operation tag - it is abnormal case, then stop parsing
    if (!isTheSameOperationTags(startTag, endTag))
      return null;
    // we expect at least 2 steps to complete the current operation parsing
    if (i + 1 >= steps.length)
      return null;
    const invertedOperationStart = parseOperationTag(steps[i++])
    // if the next step is not suitable for the inverted end operation tag, then stop parsing
    if (!isTheSameOperationTags(invertedOperationStart, invertedOperationEnd))
      return null;
    // if th next step is not reset operation tag, then stop parsing
    if (!isResetOperationStep(steps[i++]))
      return null;

    // add operation
    operations.push({
      operationName: startTag.operationName,
      firstStepIndex: startStepIndex,
      firstInnerStepIndex: startStepIndex + 3,
      lastInnerStepIndex: endStepIndex - 1,
      lastStepIndex: endStepIndex + 2,
    });
    // clear data
    startTag = null;
    endTag = null;
  }
  return operations.length ? operations : null;
}

/** Creates a specific step on the root ProseMirror node which indicates that an operation starts or ends.
 * So all steps in the stack between start and end are related to the operation.
 * @param transaction The transaction that contains operation steps.
 * @param operationName The name of the operation.
 * @param type Indicates if this is a start or end of the operation.
 */
function setOperationTag(tr: Transaction, operationName: string, type: OperationTagType): void {
  const tag: OperationTag = { operationName, type };
  const data = JSON.stringify(tag);
  tr.setNodeAttribute(0, OperationAttrName, data);
}

/** Creates a specific step on the root ProseMirror node which allows to get the correct start and end markers for inverted operations.
 * @param transaction The transaction that contains operation steps.
 */
function resetOperationTag(tr: Transaction): void {
  tr.setNodeAttribute(0, OperationAttrName, undefined);
}

function parseOperationTag(step: Step): OperationTag | undefined {
  if (!(step instanceof AttrStep) || step.attr !== OperationAttrName) return;
  const tag = step.value ? JSON.parse(step.value) as OperationTag : null;
  if ('operationName' in tag)
    return tag;
}

function isStartOperationTag(startOperationTag: OperationTag): boolean {
  return (startOperationTag.type === 'start');
}

function isTheSameOperationTags(startTag: OperationTag, endTag: OperationTag): boolean {
  if (!startTag || !endTag)
    return false;
  return (startTag.type === 'start') && (endTag.type === 'end') && (startTag.operationName === endTag.operationName);
}

function isResetOperationStep(step: Step): boolean {
  if (!(step instanceof AttrStep) || step.attr !== OperationAttrName) return;
  return step.attr === OperationAttrName && step.value === undefined;
}
